/**
 * Custom nav icons — unique to Meridian.
 * Each icon has meaning tied to the feature it represents.
 */

interface IconProps {
  size?: number;
  strokeWidth?: number;
  className?: string;
  style?: React.CSSProperties;
}

/* People: front person with a second person behind — depth, not split */
export function PeopleIcon({
  size = 24,
  strokeWidth = 1.5,
  className,
  style,
}: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      style={style}
    >
      <circle cx="10" cy="8" r="3.5" />
      <path d="M3.5 21v-1a5.5 5.5 0 0 1 5.5-5.5h2a5.5 5.5 0 0 1 5.5 5.5v1" />
      <circle cx="17.5" cy="7.5" r="2.5" opacity="0.35" />
      <path d="M20 14a3.5 3.5 0 0 1 2.5 3.5V21" opacity="0.35" />
    </svg>
  );
}

/* Circles: three overlapping rings — community, bonds */
export function CirclesIcon({
  size = 24,
  strokeWidth = 1.5,
  className,
  style,
}: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      style={style}
    >
      <circle cx="10" cy="10" r="6" />
      <circle cx="15" cy="10" r="6" opacity="0.6" />
      <circle cx="12.5" cy="15" r="6" opacity="0.35" />
    </svg>
  );
}

/* Paths: branching trail with nodes — who knows who */
export function PathsIcon({ size = 24, strokeWidth = 1.5, className, style }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      style={style}
    >
      <circle cx="4" cy="12" r="2" fill="currentColor" stroke="none" opacity="0.5" />
      <circle cx="12" cy="5" r="2" fill="currentColor" stroke="none" opacity="0.5" />
      <circle cx="12" cy="19" r="2" fill="currentColor" stroke="none" opacity="0.5" />
      <circle cx="20" cy="12" r="2" fill="currentColor" stroke="none" opacity="0.5" />
      <path d="M6 12h4M16 12h2" />
      <path d="M12 7v4M12 15v2" />
      <path d="M9.5 9.5L6 12M14.5 9.5L18 12" />
    </svg>
  );
}

/* Overview: orbital eye — see the whole person at a glance */
export function OverviewIcon({
  size = 24,
  strokeWidth = 1.5,
  className,
  style,
}: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      style={style}
    >
      <circle cx="12" cy="12" r="3" />
      <circle cx="12" cy="12" r="8" strokeDasharray="4 3" opacity="0.4" />
      <circle cx="12" cy="12" r="1" fill="currentColor" stroke="none" />
    </svg>
  );
}

/* Details: layered card — structured info */
export function DetailsIcon({
  size = 24,
  strokeWidth = 1.5,
  className,
  style,
}: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      style={style}
    >
      <rect x="4" y="4" width="16" height="16" rx="3" />
      <line x1="8" y1="9" x2="16" y2="9" opacity="0.6" />
      <line x1="8" y1="13" x2="14" y2="13" opacity="0.4" />
      <line x1="8" y1="17" x2="11" y2="17" opacity="0.25" />
    </svg>
  );
}

/* Interactions: two arrows crossing — exchange, back-and-forth history */
export function InteractionsIcon({
  size = 24,
  strokeWidth = 1.5,
  className,
  style,
}: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      style={style}
    >
      <path d="M4 8h12" />
      <polyline points="13 5 16 8 13 11" />
      <path d="M20 16H8" opacity="0.5" />
      <polyline points="11 13 8 16 11 19" opacity="0.5" />
    </svg>
  );
}
